import Spinner from '../../components/Spinner';
import React, { useEffect, useState, useRef } from 'react';
import { locomotorasService, adblueService } from '../../services/api';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  LineElement, PointElement, Title, Tooltip, Legend, Filler
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const ALERTA_LITROS = 105;

function estadoAdblue(litros, kmDia, consumo) {
  const litrosDia = (kmDia || 300) * (consumo || 0.058);
  const diasRestantes = litros > 0 ? (litros - ALERTA_LITROS) / litrosDia : 0;
  if (litros < ALERTA_LITROS) return { estado: 'CRITICO', color: '#a32d2d', bg: '#fcebeb', border: '#e24b4a', dias: 0 };
  if (diasRestantes < 7) return { estado: 'ALERTA', color: '#854f0b', bg: '#faeeda', border: '#ef9f27', dias: Math.round(diasRestantes) };
  return { estado: 'OK', color: '#3b6d11', bg: '#eaf3de', border: '#97c459', dias: Math.round(diasRestantes) };
}

function prediccion(litros, kmDia, consumo, dias = 15) {
  const litrosDia = (kmDia || 300) * (consumo || 0.058);
  const hoy = new Date();
  return Array.from({ length: dias }, (_, i) => {
    const fecha = new Date(hoy);
    fecha.setDate(hoy.getDate() + i + 1);
    return {
      fecha: fecha.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }),
      litros: Math.max(0, litros - litrosDia * (i + 1))
    };
  });
}

function TarjetaLoco({ loco, onActualizar }) {
  const [expandida, setExpandida] = useState(false);
  const [historial, setHistorial] = useState([]);
  const kmDia = loco.servicio_asignado?.km_aproximados || 300;
  const consumo = 0.058;
  const litros = loco.litros_adblue || 0;
  const cap = loco.capacidad_adblue || 530;
  const pct = Math.min(100, (litros / cap) * 100);
  const { estado, color, bg, border, dias } = estadoAdblue(litros, kmDia, consumo);
  const pred = prediccion(litros, kmDia, consumo);
  const litrosDia = kmDia * consumo;

  useEffect(() => {
    if (expandida && loco.tiene_adblue) {
      adblueService.getByLocomotora(loco.id).then(r => setHistorial(r.data.slice(-30)));
    }
  }, [expandida, loco.id, loco.tiene_adblue]);

  if (!loco.tiene_adblue) return null;

  const chartPredData = {
    labels: pred.map(p => p.fecha),
    datasets: [
      {
        type: 'bar',
        label: 'Litros estimados',
        data: pred.map(p => p.litros),
        backgroundColor: pred.map(p => p.litros < ALERTA_LITROS ? '#e24b4a' : p.litros < 200 ? '#ef9f27' : '#639922'),
        borderRadius: 4,
      },
      {
        type: 'line',
        label: 'Umbral alerta',
        data: Array(15).fill(ALERTA_LITROS),
        borderColor: '#ef9f27',
        borderDash: [4, 4],
        borderWidth: 1.5,
        pointRadius: 0,
      }
    ]
  };

  const chartHistData = historial.length > 0 ? {
    labels: historial.map(h => new Date(h.fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })),
    datasets: [{
      label: 'Litros',
      data: historial.map(h => h.litros),
      borderColor: '#378ADD',
      backgroundColor: 'rgba(55,138,221,0.08)',
      borderWidth: 2,
      pointRadius: 2,
      fill: true,
      tension: 0.3,
    }]
  } : null;

  const chartOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: '#888', font: { size: 10 }, maxRotation: 45 }, grid: { color: 'rgba(0,0,0,0.05)' } },
      y: { ticks: { color: '#888', font: { size: 11 } }, grid: { color: 'rgba(0,0,0,0.05)' }, min: 0 }
    }
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${estado !== 'OK' ? border : '#e5e5e5'}`, overflow: 'hidden', marginBottom: 12 }}>
      <div style={{ padding: '16px 18px', cursor: 'pointer' }} onClick={() => setExpandida(!expandida)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 16 }}>{loco.numero}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
              {loco.servicio_asignado ? loco.servicio_asignado.codigo : 'Sin servicio asignado'} · {litrosDia.toFixed(1)} L/día
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: bg, color, border: `1px solid ${border}`, borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 500 }}>
              {estado === 'CRITICO' ? 'CRITICO' : estado === 'ALERTA' ? `ALERTA · ${dias}d` : `OK · ${dias}d`}
            </span>
            <span style={{ color: '#aaa', fontSize: 16 }}>{expandida ? '▲' : '▼'}</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, height: 8, background: '#f0f0f0', borderRadius: 4 }}>
            <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color, minWidth: 70, textAlign: 'right' }}>{litros.toFixed(1)} L</div>
          <div style={{ fontSize: 11, color: '#aaa', minWidth: 35 }}>{Math.round(pct)}%</div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          <div style={{ fontSize: 12, color: '#888' }}>Cap: <strong style={{ color: '#333' }}>{cap} L</strong></div>
          <div style={{ fontSize: 12, color: '#888' }}>KM: <strong style={{ color: '#333' }}>{(loco.km_actuales || 0).toLocaleString('es-ES')}</strong></div>
          {estado !== 'CRITICO' && (
            <div style={{ fontSize: 12, color: '#888' }}>
              Alerta: <strong style={{ color }}>{new Date(Date.now() + dias * 86400000).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' })}</strong>
            </div>
          )}
        </div>
      </div>

      {expandida && (
        <div style={{ borderTop: '1px solid #f0f0f0', padding: '16px 18px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, justifyContent: 'flex-end' }}>
            <button onClick={() => onActualizar(loco, false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>
              ✏️ Corrección
            </button>
            <button onClick={() => onActualizar(loco, true)} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#1a1a1a', color: '#fff', cursor: 'pointer', fontSize: 13 }}>
              💧 Repostaje
            </button>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 8 }}>Prediccion 15 dias</div>
            <div style={{ height: 180 }}>
              <Bar data={chartPredData} options={chartOptions} />
            </div>
          </div>

          {chartHistData && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 8 }}>Historial</div>
              <div style={{ height: 150 }}>
                <Line data={chartHistData} options={chartOptions} />
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#555', marginBottom: 8 }}>Proximos 15 dias</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {pred.map((p, i) => (
                <div key={i} style={{ background: p.litros < ALERTA_LITROS ? '#fcebeb' : p.litros < 200 ? '#faeeda' : '#f8f8f6', borderRadius: 6, padding: '6px 8px', fontSize: 11 }}>
                  <div style={{ color: '#888' }}>{p.fecha}</div>
                  <div style={{ fontWeight: 500, color: p.litros < ALERTA_LITROS ? '#a32d2d' : p.litros < 200 ? '#854f0b' : '#333' }}>{p.litros.toFixed(0)} L</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdBlue() {
  const [locomotoras, setLocomotoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { cargar(); }, []);

  const cargar = () => {
    setLoading(true);
    locomotorasService.getAll()
      .then(r => setLocomotoras(r.data.filter(l => l.activa && l.tiene_adblue)))
      .finally(() => setLoading(false));
  };

  const abrirModal = (loco, esRepostaje) => {
    setModal({ loco, esRepostaje });
    setForm({
      locomotora_id: loco.id,
      fecha: new Date().toISOString().split('T')[0],
      km: loco.km_actuales || '',
      litros: loco.litros_adblue || '',
      es_repostaje: esRepostaje,
      es_correccion: !esRepostaje,
      notas: ''
    });
  };

  const guardar = async () => {
    if (!form.litros || !form.km) { setMsg('Rellena los litros y los km'); return; }
    setSaving(true);
    try {
      await adblueService.crear({
        locomotora_id: form.locomotora_id,
        fecha: new Date(form.fecha).toISOString(),
        km: parseFloat(form.km),
        litros: parseFloat(form.litros),
        es_repostaje: form.es_repostaje,
        es_correccion: form.es_correccion,
        notas: form.notas || null,
      });
      setMsg('Guardado correctamente');
      setModal(null);
      cargar();
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setMsg('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;

  const criticas = locomotoras.filter(l => (l.litros_adblue || 0) < ALERTA_LITROS);
  const alertas = locomotoras.filter(l => {
    const litros = l.litros_adblue || 0;
    const kmDia = l.servicio_asignado?.km_aproximados || 300;
    const consumo = 0.058;
    const litrosDia = kmDia * consumo;
    const dias = (litros - ALERTA_LITROS) / litrosDia;
    return litros >= ALERTA_LITROS && dias < 7;
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 500, margin: 0 }}>Control AdBlue</h2>
        <p style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Estado y prediccion de la flota · umbral alerta {ALERTA_LITROS} L</p>
      </div>

      {msg && <div style={{ background: '#eaf3de', border: '1px solid #97c459', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#3b6d11' }}>{msg}</div>}

      {criticas.length > 0 && (
        <div style={{ background: '#fcebeb', border: '1px solid #e24b4a', borderRadius: 8, padding: '12px 16px', marginBottom: 12, fontSize: 13, color: '#a32d2d', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>⚠</span>
          <strong>Estado critico:</strong> {criticas.map(l => l.numero).join(', ')} — Revisar urgente
        </div>
      )}

      {alertas.length > 0 && (
        <div style={{ background: '#faeeda', border: '1px solid #ef9f27', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#854f0b', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>⚡</span>
          <strong>Proximas alertas:</strong> {alertas.map(l => l.numero).join(', ')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
        {locomotoras
          .sort((a, b) => (a.litros_adblue || 0) - (b.litros_adblue || 0))
          .map(l => (
            <TarjetaLoco key={l.id} loco={l} onActualizar={abrirModal} />
          ))
        }
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 400, maxWidth: '95vw' }}>
            <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
              {modal.esRepostaje ? '💧 Repostaje' : '✏️ Correccion'} — {modal.loco.numero}
            </h3>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>
              Litros actuales: <strong>{(modal.loco.litros_adblue || 0).toFixed(1)} L</strong>
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Fecha</label>
                <input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>KM odometro</label>
                <input type="number" value={form.km} onChange={e => setForm({ ...form, km: e.target.value })}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Litros totales en deposito</label>
              <input type="number" value={form.litros} onChange={e => setForm({ ...form, litros: e.target.value })}
                placeholder="Ej: 380"
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#555', marginBottom: 4 }}>Notas (opcional)</label>
              <input type="text" value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setModal(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Cancelar</button>
              <button onClick={guardar} disabled={saving} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#1a1a1a', color: '#fff', cursor: 'pointer', fontSize: 13, opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
